import React, { useState, useEffect, useRef } from 'react';
import { 
  getConversation, 
  sendMessage, 
  markMessagesAsRead,
  subscribeToConversation
} from '../services/messageService';
import '../styles/ChatWindow.css';
import ErrorNotification from './ErrorNotification';

// Regex patterns to detect various link formats
const URL_PATTERNS = [
  /https?:\/\/[^\s]+/gi,        // http:// or https:// links
  /www\.[^\s]+\.[^\s]+/gi,      // www.domain.com
  /[^\s@]+\.[a-z]{2,}(?:\/[^\s]*)*/gi  // domain.com or domain.com/path
];

// Function to check if text contains links
const containsLinks = (text) => {
  return URL_PATTERNS.some(pattern => pattern.test(text));
};

// Function to sanitize input text by removing links
const sanitizeText = (text) => {
  let sanitized = text;
  URL_PATTERNS.forEach(pattern => {
    sanitized = sanitized.replace(pattern, '[link removed]');
  });
  return sanitized;
};

const ChatWindow = ({ walletAddress, friend, onClose, style }) => {
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [linkWarning, setLinkWarning] = useState('');
  const messagesEndRef = useRef(null);
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    let unsubscribe = null;
    
    const initializeChat = async () => {
      setLoading(true);
      try {
        // Mark messages as read when opening chat
        await markMessagesAsRead(walletAddress, friend.walletAddress);
        
        // Subscribe to conversation updates
        unsubscribe = subscribeToConversation(
          walletAddress, 
          friend.walletAddress, 
          (updatedMessages) => {
            setMessages(updatedMessages);
            setLoading(false);
            setError('');
            scrollToBottom();
          }
        );
      } catch (err) {
        setError('Failed to load messages');
        setLoading(false);
        setErrorMessage('Failed to load messages: ' + (err.message || 'Unknown error'));
      }
    };

    if (friend && friend.walletAddress) {
      initializeChat();
    }
    
    // Cleanup subscription on unmount
    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, [walletAddress, friend.walletAddress]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleInputChange = (e) => {
    const text = e.target.value;
    setNewMessage(text);
    
    // Check for links in the input
    if (containsLinks(text)) {
      setLinkWarning('Links are not allowed in messages');
    } else {
      setLinkWarning('');
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !friend.walletAddress) return;
  
    // Prevent sending if message contains links
    if (containsLinks(newMessage)) {
      setLinkWarning('Links are not allowed. Please remove links before sending.');
      setErrorMessage('Links are not allowed in messages. Please remove any links before sending.');
      return;
    }
  
    try {
      // Send message via Firebase
      await sendMessage(walletAddress, friend.walletAddress, newMessage);
      setNewMessage('');
      setLinkWarning('');
      scrollToBottom();
    } catch (err) {
      setError('Failed to send message');
      setErrorMessage('Failed to send message: ' + (err.message || 'Unknown error'));
    }
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return '';
    return new Date(timestamp).toLocaleTimeString([], {
      hour: 'numeric',
      minute: '2-digit',
    });
  };

  return (
    <div className="chat-window cyberpunk-panel" style={style}>
      <div className="chat-header">
        <h3>{friend.username || 'Unknown'}</h3>
        <button className="close-btn" onClick={onClose}>×</button>
      </div>

      <div className="chat-messages">
        {loading ? (
          <div className="loading-container">
            <div className="loading-indicator"></div>
            <p>Loading messages...</p>
          </div>
        ) : error ? (
          <p className="error-message">{error}</p>
        ) : messages.length === 0 ? (
          <p className="no-messages">No messages yet. Say hello!</p>
        ) : (
          <>
            {messages.map((msg, index) => (
              <div
                key={msg.id || index}
                className={`message ${msg.senderWallet === walletAddress ? 'sent' : 'received'}`}
              >
                <p>{msg.content}</p>
                <span className="message-time">{formatTime(msg.createdAt)}</span>
              </div>
            ))}
            <div ref={messagesEndRef} />
          </>
        )}
      </div>

      <form className="chat-input-form" onSubmit={handleSendMessage}>
        {linkWarning && <div className="link-warning">{linkWarning}</div>}
        <input
          className="chat-input"
          value={newMessage}
          onChange={handleInputChange}
          placeholder="Type a message..."
        />
        <button
          className="cyber-button send-btn"
          type="submit"
          disabled={!newMessage.trim() || containsLinks(newMessage)}
        >
          Send
        </button>
      </form>
      <ErrorNotification 
        message={errorMessage}
        onClose={() => setErrorMessage('')}
      />
    </div>
  );
};

export default ChatWindow;